import flexibleHouseCarouselTemplate from '@templates/website/search/partials/flexibleHouseCarouselTemplate.html.njk';
import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import convertToCurrency from '@naturehouse/nh-essentials/lib/currency/currencyHelper';
import { onIntersectionObserver } from '@naturehouse/nh-essentials/lib/events/eventHandling';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import GoogleAnalyticsEvents from '../../../common/GoogleAnalyticsEvents';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import { convertPriceStringToNumber, getPosition } from '../../../util/dataLayer';
import { FeatureToggle, isFeatureEnabled } from '../../../util/featureToggle';
import GTMSelectItemModel from '../../../gtm/models/GTMSelectItemModel';
import GTM from '../../../gtm/GTM';
import ArrivalDepartureFilter from '../store/ArrivalDepartureFilter';
import FlexibleArrivalDateFilter from '../store/FlexibleArrivalDateFilter';
import HouseSearchApi from '../store/HouseSearchApi';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import { calculateNumberOfNights } from '../../../gtm/GTMHelpers';

export default class FlexibleHouseCarousel extends HTMLElement implements Observer {
    #houses: SearchResponseHouse[] = [];

    readonly #filters = HouseSearchFilters.getInstance();

    readonly #houseSearchListItems = HouseSearchListItems.getInstance();

    readonly #apiRequestState = ApiRequestState.getInstance();

    #translations: Record<string, string> = {};

    public connectedCallback(): void {
        this.#apiRequestState.attach(this);

        this.#translations = JSON.parse(this.dataset.translations || '{}');

        if ('IntersectionObserver' in window) {
            const options: IntersectionObserverOptionsInterface = {
                root: null,
                rootMargin: '400px 0px 0px',
                threshold: 0
            };

            onIntersectionObserver(
                this,
                () => {
                    this.#initialize();
                },
                options
            );
            return;
        }
        this.#initialize();
    }

    public disconnectedCallback(): void {
        this.#apiRequestState.detach(this);
    }

    #initialize(): void {
        this.#performSearch();
    }

    public update(subject: ApiRequestState): void {
        if (subject.state !== ApiRequestState.REQUEST_SUCCESS) {
            return;
        }

        this.#performSearch();
    }

    #handleProductClick(event: Event, index: number, anchor: HTMLAnchorElement): void {
        event.preventDefault();

        const card: HTMLElement = anchor.closest('[data-house-id]') as HTMLElement;
        const productId = Number(card.dataset.houseId || 0);

        const house: SearchResponseHouse | undefined = this.#houses.find(
            (h: SearchResponseHouse) => h.id === productId
        );

        if (!house) {
            return;
        }

        if (isFeatureEnabled(FeatureToggle.GOOGLE_TAG_MANAGER)) {
            const numberOfNights = calculateNumberOfNights({
                arrivalDate: house.price.startDate,
                departureDate: house.price.endDate
            });

            GTM.track(
                GTMSelectItemModel.parseToGTMData({
                    listId: 'flexible_search_results',
                    listName: 'Flexible Search Results',
                    id: house.id.toString(),
                    title: house.title,
                    country: house.location.country,
                    region: house.location.region ?? '',
                    department: house.location.department ?? '',
                    place: house.location.place,
                    quantity: numberOfNights ?? 1,
                    discount: house.price.discountPerNight ?? 0,
                    index: index,
                    pricePerNight: convertPriceStringToNumber(house.price.dayPrice) || 0,
                    instantBooking: false
                })
            );
        } else {
            const houseItem: HouseSearchListItem = {
                id: house.id,
                externalId: house.externalId ?? '',
                websiteUrl: house.websiteUrl,
                extraCostsUrl: house.extraCostsUrl,
                title: house.title,
                subtitle: house.locationTitle,
                price: house.price,
                totalFormatted: '?????????',
                dayPriceFormatted: '?????????',
                startDateFormatted: '?????????',
                dayPriceWithDiscountFormatted: '?????????',
                totalWithDiscountFormatted: '?????????',
                imageUrl: house.imageUrl,
                persons: house.persons,
                bedrooms: house.bedrooms,
                features: house.features,
                natureRating: house.natureRating,
                distance: house.distance,
                images: house.images,
                region: house.location.region,
                country: house.location.country,
                department: house.location.department,
                naturalArea: house.location.naturalArea,
                place: house.location.place
            };

            GoogleAnalyticsEvents.productListClick(
                'Flexible Search Results',
                Number(house.price.dayPrice),
                houseItem,
                getPosition(index + 1)
            );
        }

        window.open(anchor.href, anchor.target);
    }

    #performSearch(): void {
        const flexibleArrivalDateFilter = this.#filters.get(
            FlexibleArrivalDateFilter.NAME
        ) as FlexibleArrivalDateFilter;

        const dateFilter = this.#filters.get(ArrivalDepartureFilter.NAME) as ArrivalDepartureFilter;

        const flexibleArrivalDate: number = parseInt(
            flexibleArrivalDateFilter.parameters.flexibleArrivalDate || '0',
            10
        );

        if (
            flexibleArrivalDate > 0 ||
            !dateFilter.parameters.arrivalDate ||
            !dateFilter.parameters.departureDate
        ) {
            return;
        }

        const url: URL = new URL(window.location.origin + window.location.pathname);
        Object.keys(this.#filters.activeParams).forEach((key: string) => {
            const value: string = this.#filters.activeParams[key];
            url.searchParams.set(key, value);
        });

        const requestUrl: URL = HouseSearchApi.getSearchUrl(this.#filters.activeParams);
        const limit = Number(requestUrl.searchParams.get('limit') || 0);
        requestUrl.searchParams.set('limit', `${limit + 20}`);
        requestUrl.searchParams.set('flexibleArrivalDate', '1');

        this.classList.add('loading-spinner');
        HttpClient.get(requestUrl.href).then((response) => {
            const searchResponse: SearchResponse = response as unknown as SearchResponse;
            const houses: SearchResponseHouse[] = searchResponse.houses;
            const container = this.closest('.search__flexible-results') as HTMLElement;

            if (!container) {
                return;
            }

            if (!houses) {
                this.classList.remove('loading-spinner');
                container.hidden = true;
                return;
            }

            const currentSearchHouseIds: number[] = this.#houseSearchListItems.houses.map(
                (house: HouseSearchListItem) => house.id
            );

            const newHouses: SearchResponseHouse[] = houses.filter(
                (house: SearchResponseHouse) => !currentSearchHouseIds.includes(Number(house.id))
            );

            if (!newHouses.length) {
                this.classList.remove('loading-spinner');
                container.hidden = true;
                return;
            }

            for (let index = 0; index < newHouses.length; index++) {
                const house: SearchResponseHouse = newHouses[index];
                const price: number = house.price.dayPriceWithDiscount
                    ? Number(house.price.dayPriceWithDiscount)
                    : Number(house.price.dayPrice);

                house.formattedPrice = price ? convertToCurrency(price) : '';
                house.priceTitle = house.formattedPrice ? this.#translations.pricePerNight : '';
            }

            this.#houses = newHouses;
            this.dataset.searchUrl = url.href;
            this.innerHTML = flexibleHouseCarouselTemplate.render({
                translations: this.#translations,
                houses: this.#houses,
                isLoggedIn: this.dataset.isLoggedIn
            });

            this.classList.remove('loading-spinner');
            container.hidden = false;

            const houseAnchors: HTMLAnchorElement[] = Array.from(
                this.querySelectorAll('.nh-card--small a')
            );

            for (let index = 0; index < houseAnchors.length; index++) {
                const anchor = houseAnchors[index];
                anchor.addEventListener('click', (event: Event) => {
                    this.#handleProductClick(event, index, anchor);
                });
            }
        });
    }
}

if (!customElements.get('nh-flexible-house-carousel')) {
    customElements.define('nh-flexible-house-carousel', FlexibleHouseCarousel);
}
